import React, { createContext, useState, useEffect, useMemo } from "react";

import {
  IlpService,
  ServiceStatusEnum,
  IIlpServiceState,
  IIlpWithLessons,
  ILesson,
  ILessonMetadata,
  IIlp
} from "@imaginelearning/mps-api-service";

const defaultILPState: IIlpServiceState<IIlpWithLessons> = {
  status: ServiceStatusEnum.LOADING,
  response: ""
};
const defaultILPMetadataState: IIlpServiceState<ILessonMetadata[]> = {
  status: ServiceStatusEnum.LOADING,
  response: []
};
const defaultDispatchString: React.Dispatch<React.SetStateAction<string>> = (val) => val;
const defaultDispatchBoolean: React.Dispatch<React.SetStateAction<boolean>> = (val) => val;

const defaultState = {
  ilpId: "",
  ilpState: defaultILPState,
  setIlpId: defaultDispatchString,
  getUpdatedIlp: defaultDispatchString,
  showTokenExpiredAlert: false,
  currentLessonMetadata: defaultILPMetadataState,
  setShowTokenExpiredAlert: defaultDispatchBoolean,
  standardCode: ""
};

export const IlpContext = createContext(defaultState);

export function IlpProvider(props: any) {
  const [ilpState, setIlp] = useState<IIlpServiceState<IIlpWithLessons>>(defaultILPState);
  const [ilpId, setIlpId] = useState("");
  const [currentLessonMetadata, setCurrentLessonMetadata] =
    useState<IIlpServiceState<ILessonMetadata[]>>(defaultILPMetadataState);
  const [forceRefetch, getUpdatedIlp] = useState("");
  const [showTokenExpiredAlert, setShowTokenExpiredAlert] = useState(false);
  const [standardCode, setStandardCode] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      const result: IIlpServiceState<IIlpWithLessons> = await IlpService.getIlpIncludeAssignedLessons(ilpId);
      const currentMetadata = await fetchCurrentMetaData(result);
      setCurrentLessonMetadata(currentMetadata);
      setIlp(result);
    };

    const fetchCurrentMetaData = async (data: IIlpServiceState<IIlpWithLessons>) => {
      const { userId, standardCode, lessons, gradeLevel } = data.response as IIlpWithLessons;
      setStandardCode(standardCode);
      const studentId = userId;
      let metaData: IIlpServiceState<ILessonMetadata[]> = {
        ...defaultILPMetadataState,
        status: ServiceStatusEnum.LOADED
      };
      if (gradeLevel >= 6) {
        const currentLesson = lessons.find((lesson: ILesson) => lesson.isNext);
        if (currentLesson) {
          const { pathId } = currentLesson as ILesson;
          metaData = await IlpService.getLessonMetadataByPathId(pathId, studentId, standardCode);
        }
      }

      return metaData;
    };

    if (ilpId) {
      setIlp(defaultILPState);
      fetchData();
    }
  }, [ilpId, forceRefetch]);

  const value = useMemo(
    () => ({
      ilpId,
      ilpState,
      setIlpId,
      getUpdatedIlp,
      currentLessonMetadata,
      showTokenExpiredAlert,
      setShowTokenExpiredAlert,
      standardCode
    }),
    [ilpId, ilpState, showTokenExpiredAlert, currentLessonMetadata, standardCode]
  );

  return <IlpContext.Provider value={value} {...props} />;
}
